/**
 * Voluum flow “weekdays” time zone options — exact labels and order from Voluum’s
 * picker, each row bound to a canonical IANA id for `timeZone.code`.
 */

export interface VoluumFlowTimezoneEntry {
  iana: string;
  label: string;
}

const ROWS: ReadonlyArray<readonly [string, string]> = [
  ['Etc/GMT+12', '(UTC-12:00) International Date Line West'],
  ['Etc/GMT+11', '(UTC-11:00) Coordinated Universal Time-11'],
  ['Pacific/Honolulu', '(UTC-10:00) Hawaii'],
  ['America/Anchorage', '(UTC-08:00) Alaska'],
  ['America/Tijuana', '(UTC-07:00) Baja California'],
  ['America/Los_Angeles', '(UTC-07:00) Pacific Time (US & Canada)'],
  ['America/Phoenix', '(UTC-07:00) Arizona'],
  ['America/Chihuahua', '(UTC-06:00) Chihuahua, La Paz, Mazatlan'],
  ['America/Denver', '(UTC-06:00) Mountain Time (US & Canada)'],
  ['America/Guatemala', '(UTC-06:00) Central America'],
  ['America/Mexico_City', '(UTC-06:00) Guadalajara, Mexico City, Monterrey'],
  ['America/Regina', '(UTC-06:00) Saskatchewan'],
  ['America/Chicago', '(UTC-05:00) Central Time (US & Canada)'],
  ['America/Bogota', '(UTC-05:00) Bogota, Lima, Quito, Rio Branco'],
  ['America/Chetumal', '(UTC-05:00) Chetumal'],
  ['America/New_York', '(UTC-04:00) Eastern Time (US & Canada)'],
  ['America/Indiana/Indianapolis', '(UTC-04:00) Indiana (East)'],
  ['America/Caracas', '(UTC-04:00) Caracas'],
  ['America/Asuncion', '(UTC-04:00) Asuncion'],
  ['America/Cuiaba', '(UTC-04:00) Cuiaba'],
  ['America/La_Paz', '(UTC-04:00) Georgetown, La Paz, Manaus, San Juan'],
  ['America/Santiago', '(UTC-04:00) Santiago'],
  ['America/Halifax', '(UTC-03:00) Atlantic Time (Canada)'],
  ['America/Sao_Paulo', '(UTC-03:00) Brasilia'],
  ['America/Buenos_Aires', '(UTC-03:00) Buenos Aires'],
  ['America/Cayenne', '(UTC-03:00) Cayenne, Fortaleza'],
  ['America/Montevideo', '(UTC-03:00) Montevideo'],
  ['America/Bahia', '(UTC-03:00) Salvador'],
  ['America/St_Johns', '(UTC-02:30) Newfoundland'],
  ['Etc/GMT+2', '(UTC-02:00) Coordinated Universal Time-02'],
  ['America/Nuuk', '(UTC-01:00) Greenland'],
  ['Atlantic/Cape_Verde', '(UTC-01:00) Cabo Verde Is.'],
  ['Atlantic/Azores', '(UTC) Azores'],
  ['Etc/UTC', '(UTC) Coordinated Universal Time'],
  ['Atlantic/Reykjavik', '(UTC) Monrovia, Reykjavik'],
  ['Africa/Casablanca', '(UTC+01:00) Casablanca'],
  ['Europe/London', '(UTC+01:00) Dublin, Edinburgh, Lisbon, London'],
  ['Africa/Lagos', '(UTC+01:00) West Central Africa'],
  ['Europe/Berlin', '(UTC+02:00) Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna'],
  ['Europe/Belgrade', '(UTC+02:00) Belgrade, Bratislava, Budapest, Ljubljana, Prague'],
  ['Europe/Paris', '(UTC+02:00) Brussels, Copenhagen, Madrid, Paris'],
  ['Europe/Sarajevo', '(UTC+02:00) Sarajevo, Skopje, Warsaw, Zagreb'],
  ['Africa/Windhoek', '(UTC+02:00) Windhoek'],
  ['Africa/Johannesburg', '(UTC+02:00) Harare, Pretoria'],
  ['Europe/Kaliningrad', '(UTC+02:00) Kaliningrad (RTZ 1)'],
  ['Africa/Tripoli', '(UTC+02:00) Tripoli'],
  ['Asia/Amman', '(UTC+03:00) Amman'],
  ['Europe/Bucharest', '(UTC+03:00) Athens, Bucharest'],
  ['Asia/Beirut', '(UTC+03:00) Beirut'],
  ['Africa/Cairo', '(UTC+03:00) Cairo'],
  ['Asia/Damascus', '(UTC+03:00) Damascus'],
  ['Europe/Kyiv', '(UTC+03:00) Helsinki, Kyiv, Riga, Sofia, Tallinn, Vilnius'],
  ['Europe/Istanbul', '(UTC+03:00) Istanbul'],
  ['Asia/Jerusalem', '(UTC+03:00) Jerusalem'],
  ['Asia/Baghdad', '(UTC+03:00) Baghdad'],
  ['Asia/Riyadh', '(UTC+03:00) Kuwait, Riyadh'],
  ['Europe/Minsk', '(UTC+03:00) Minsk'],
  ['Europe/Moscow', '(UTC+03:00) Moscow, St. Petersburg, Volgograd (RTZ 2)'],
  ['Africa/Nairobi', '(UTC+03:00) Nairobi'],
  ['Asia/Tehran', '(UTC+03:30) Tehran'],
  ['Asia/Dubai', '(UTC+04:00) Abu Dhabi, Muscat'],
  ['Asia/Baku', '(UTC+04:00) Baku'],
  ['Europe/Samara', '(UTC+04:00) Izhevsk, Samara (RTZ 3)'],
  ['Indian/Mauritius', '(UTC+04:00) Port Louis'],
  ['Asia/Tbilisi', '(UTC+04:00) Tbilisi'],
  ['Asia/Yerevan', '(UTC+04:00) Yerevan'],
  ['Asia/Kabul', '(UTC+04:30) Kabul'],
  ['Asia/Tashkent', '(UTC+05:00) Ashgabat, Tashkent'],
  ['Asia/Yekaterinburg', '(UTC+05:00) Ekaterinburg (RTZ 4)'],
  ['Asia/Karachi', '(UTC+05:00) Islamabad, Karachi'],
  ['Asia/Almaty', '(UTC+05:00) Astana'],
  ['Asia/Kolkata', '(UTC+05:30) Chennai, Kolkata, Mumbai, New Delhi'],
  ['Asia/Colombo', '(UTC+05:30) Sri Jayawardenepura'],
  ['Asia/Kathmandu', '(UTC+05:45) Kathmandu'],
  ['Asia/Dhaka', '(UTC+06:00) Dhaka'],
  ['Asia/Yangon', '(UTC+06:30) Yangon (Rangoon)'],
  ['Asia/Novosibirsk', '(UTC+07:00) Novosibirsk (RTZ 5)'],
  ['Asia/Bangkok', '(UTC+07:00) Bangkok, Hanoi, Jakarta'],
  ['Asia/Krasnoyarsk', '(UTC+07:00) Krasnoyarsk (RTZ 6)'],
  ['Asia/Shanghai', '(UTC+08:00) Beijing, Chongqing, Hong Kong, Urumqi'],
  ['Asia/Irkutsk', '(UTC+08:00) Irkutsk (RTZ 7)'],
  ['Asia/Singapore', '(UTC+08:00) Kuala Lumpur, Singapore'],
  ['Australia/Perth', '(UTC+08:00) Perth'],
  ['Asia/Taipei', '(UTC+08:00) Taipei'],
  ['Asia/Ulaanbaatar', '(UTC+08:00) Ulaanbaatar'],
  ['Asia/Tokyo', '(UTC+09:00) Osaka, Sapporo, Tokyo'],
  ['Asia/Seoul', '(UTC+09:00) Seoul'],
  ['Asia/Yakutsk', '(UTC+09:00) Yakutsk (RTZ 8)'],
  ['Australia/Adelaide', '(UTC+09:30) Adelaide'],
  ['Australia/Darwin', '(UTC+09:30) Darwin'],
  ['Australia/Brisbane', '(UTC+10:00) Brisbane'],
  ['Australia/Sydney', '(UTC+10:00) Canberra, Melbourne, Sydney'],
  ['Pacific/Port_Moresby', '(UTC+10:00) Guam, Port Moresby'],
  ['Australia/Hobart', '(UTC+10:00) Hobart'],
  ['Asia/Vladivostok', '(UTC+10:00) Vladivostok, Magadan (RTZ 9)'],
  ['Asia/Magadan', '(UTC+11:00) Magadan'],
  ['Asia/Srednekolymsk', '(UTC+11:00) Chokurdakh (RTZ 10)'],
  ['Pacific/Guadalcanal', '(UTC+11:00) Solomon Is., New Caledonia'],
  ['Asia/Anadyr', '(UTC+12:00) Anadyr, Petropavlovsk-Kamchatsky (RTZ 11)'],
  ['Pacific/Auckland', '(UTC+12:00) Auckland, Wellington'],
  ['Etc/GMT-12', '(UTC+12:00) Coordinated Universal Time+12'],
  ['Pacific/Fiji', '(UTC+12:00) Fiji'],
  ['Pacific/Tongatapu', "(UTC+13:00) Nuku'alofa"],
  ['Pacific/Apia', '(UTC+13:00) Samoa'],
  ['Pacific/Kiritimati', '(UTC+14:00) Kiritimati Island'],
];

export const VOLUUM_FLOW_TIMEZONES: readonly VoluumFlowTimezoneEntry[] =
  ROWS.map(([iana, label]) => ({ iana, label }));

const LABEL_BY_IANA = new Map(
  VOLUUM_FLOW_TIMEZONES.map((e) => [e.iana, e.label]),
);

export function voluumFlowTimezoneLabel(iana: string): string | undefined {
  return LABEL_BY_IANA.get(iana);
}

export function listVoluumFlowTimezoneIds(): readonly string[] {
  return VOLUUM_FLOW_TIMEZONES.map((e) => e.iana);
}
